import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { draftsApi, exportsApi, pleadingCheckApi } from "@/lib/api";
import { useAsyncJob } from "@/lib/useAsyncJob";
import { JobProgress } from "@/components/StrategyPanel";
import { ProgressTimeline } from "@/components/ProgressTimeline";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AIAssistBanner, SourceChip } from "@/components/SourceChip";
import {
    ArrowClockwise,
    ArrowLeft,
    DownloadSimple,
    Eye,
    FloppyDisk,
    GitDiff,
    PaperPlaneTilt,
    PencilSimple,
    ShieldCheck,
    Sparkle,
    TrashSimple,
} from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function DraftDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [draft, setDraft] = useState(null);
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [saving, setSaving] = useState(false);
    const [redlineOpen, setRedlineOpen] = useState(false);
    const [diff, setDiff] = useState(null);
    const [verA, setVerA] = useState("1");
    const [verB, setVerB] = useState("1");
    const [refineInstr, setRefineInstr] = useState("");
    const [refining, setRefining] = useState(false);
    const [refineJob, setRefineJob] = useState(null);
    const [refineSeniorReview, setRefineSeniorReview] = useState(false);
    const [editing, setEditing] = useState(false);

    const load = useCallback(async () => {
        const d = await draftsApi.get(id);
        setDraft(d);
        setTitle(d.title);
        setBody(d.body);
        if (d.versions?.length) {
            setVerA(String(Math.max(1, d.current_version - 1)));
            setVerB(String(d.current_version));
        }
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    const save = async () => {
        setSaving(true);
        try {
            const updated = await draftsApi.update(id, { title, body });
            setDraft(updated);
            toast.success(`Saved · v${updated.current_version}`);
        } catch {
            toast.error("Save failed");
        }
        setSaving(false);
    };

    const runRedline = async () => {
        try {
            const d = await draftsApi.redline(id, Number(verA), Number(verB));
            setDiff(d);
            setRedlineOpen(true);
        } catch {
            toast.error("Redline failed");
        }
    };

    const revert = async (version) => {
        if (!window.confirm(`Revert draft to v${version}? (This creates a new version.)`)) return;
        const updated = await draftsApi.revert(id, version);
        setDraft(updated);
        setBody(updated.body);
        toast.success("Reverted");
    };

    const remove = async () => {
        try {
            await draftsApi.remove(id);
            toast.success("Draft deleted");
            navigate("/drafting");
        } catch {
            toast.error("Failed to delete draft");
        }
    };

    const refine = async () => {
        const instr = refineInstr.trim();
        if (!instr) {
            toast.error("Describe the change you want");
            return;
        }
        if (body !== draft?.body) {
            const ok = window.confirm(
                "You have unsaved manual edits to this draft. Refine will use the LAST SAVED version as input — your unsaved edits won't be included. Continue?"
            );
            if (!ok) return;
        }
        setRefining(true);
        setRefineJob(null);
        try {
            const submitted = await draftsApi.refine(id, {
                instructions: instr,
                senior_review: refineSeniorReview,
            });
            const seed = await draftsApi.jobStatus(submitted.job_id);
            setRefineJob(seed);
            for (let i = 0; i < 100; i++) {
                await new Promise((r) => setTimeout(r, 3000));
                const s = await draftsApi.jobStatus(submitted.job_id);
                setRefineJob(s);
                if (s.status === "completed") {
                    await load();
                    setRefineInstr("");
                    toast.success(refineSeniorReview ? "Refined + reviewed" : "Refined · new version saved");
                    setRefining(false);
                    setRefineJob(null);
                    return;
                }
                if (s.status === "failed") {
                    throw Object.assign(new Error(s.error || "Refine failed"), { status: s.error_status_code });
                }
            }
            toast.info("Still working in the background — refresh in a moment to see the new version.");
        } catch (e) {
            console.error(e);
            toast.error(e?.message || "Refine failed", { duration: 8000 });
        }
        setRefining(false);
        setRefineJob(null);
    };

    if (!draft) return <div className="p-8 text-sm text-muted-foreground">Loading draft…</div>;

    return (
        <div className="h-[calc(100vh-57px)] flex flex-col" data-testid="draft-detail">
            <div className="px-4 md:px-8 py-3 border-b border-border bg-white/80 flex items-center justify-between gap-3 flex-wrap">
                <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft size={14} /> Back</Button>
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="chip chip-doc">{draft.draft_type?.replace(/_/g, " ")}</span>
                    <span className="chip chip-external">v{draft.current_version}</span>
                    <Button variant="outline" size="sm" onClick={runRedline} data-testid="draft-redline-btn"><GitDiff size={14} /> Redline</Button>
                    <a href={exportsApi.draftDocx(id)} className="inline-flex items-center gap-1 text-xs border border-border rounded-sm px-3 h-8" data-testid="draft-export-docx"><DownloadSimple size={13} /> DOCX</a>
                    <a href={exportsApi.draftPdf(id)} className="inline-flex items-center gap-1 text-xs border border-border rounded-sm px-3 h-8" data-testid="draft-export-pdf"><DownloadSimple size={13} /> PDF</a>
                    <Button size="sm" onClick={save} disabled={saving} data-testid="draft-save-btn"><FloppyDisk size={14} /> {saving ? "Saving…" : "Save new version"}</Button>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                className="text-destructive hover:text-destructive border-destructive/30 hover:bg-destructive/10"
                                data-testid="draft-delete-btn"
                            >
                                <TrashSimple size={14} /> Delete
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Delete this draft?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    "{draft.title}" and all its versions will be permanently removed. This cannot be undone.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel data-testid="draft-delete-cancel">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                    onClick={remove}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    data-testid="draft-delete-confirm"
                                >
                                    Delete
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>

            <div className="grid grid-cols-12 flex-1 min-h-0 w-full max-w-[1600px] mx-auto">
                {/* center: editor */}
                <section className="col-span-12 lg:col-span-8 overflow-y-auto scrollbar-slim p-4 md:p-8 bg-[hsl(39,44%,97%)]">
                    <AIAssistBanner />
                    <div className="mt-4 mb-2 flex items-center gap-2 text-xs flex-wrap">
                        <SourceChip type="ai">Model-generated</SourceChip>
                        <span className="text-muted-foreground">{draft.language?.toUpperCase()} · claude-sonnet-4.5</span>
                        <div className="ml-auto inline-flex border border-border rounded-sm overflow-hidden" data-testid="view-edit-toggle">
                            <button
                                onClick={() => setEditing(false)}
                                className={`px-3 h-8 text-xs inline-flex items-center gap-1 transition-colors ${
                                    !editing ? "bg-primary text-primary-foreground" : "bg-white text-muted-foreground hover:text-foreground"
                                }`}
                                data-testid="view-mode-preview"
                            >
                                <Eye size={13} /> Preview
                            </button>
                            <button
                                onClick={() => setEditing(true)}
                                className={`px-3 h-8 text-xs inline-flex items-center gap-1 transition-colors border-l border-border ${
                                    editing ? "bg-primary text-primary-foreground" : "bg-white text-muted-foreground hover:text-foreground"
                                }`}
                                data-testid="view-mode-edit"
                            >
                                <PencilSimple size={13} /> Edit
                            </button>
                        </div>
                    </div>
                    <Input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="font-serif text-2xl md:text-3xl border-0 bg-transparent px-0 h-auto py-2 focus-visible:ring-0"
                        data-testid="draft-title-input"
                    />
                    <div className="legal-sheet">
                        {editing ? (
                            <Textarea
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                rows={30}
                                className="font-mono border-0 bg-transparent shadow-none focus-visible:ring-0 text-[13px] leading-7 resize-none px-0"
                                data-testid="draft-body-editor"
                                placeholder="Markdown source — switch to Preview to see the rendered legal document."
                            />
                        ) : (
                            <article
                                className="legal-rendered"
                                data-testid="draft-body-preview"
                                lang={draft.language === "hi" ? "hi" : draft.language === "mixed" ? "hi" : "en"}
                            >
                                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{body}</ReactMarkdown>
                            </article>
                        )}
                    </div>

                    {/* Refine with AI — iterate on the current draft via prompt */}
                    <div
                        className="mt-6 border border-border bg-white rounded-md p-4 shadow-sm"
                        data-testid="refine-panel"
                    >
                        <div className="flex items-center gap-2 mb-2">
                            <Sparkle size={16} weight="fill" className="text-amber-600" />
                            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Refine with AI</div>
                        </div>
                        <div className="text-xs text-muted-foreground mb-3 leading-relaxed">
                            Describe the change you want — e.g.,{" "}
                            <span className="italic">"Make the tone more assertive"</span>,{" "}
                            <span className="italic">"Add a clause invoking RERA Section 18 with delay interest at SBI MCLR + 2%"</span>, or{" "}
                            <span className="italic">"Translate paragraphs 4–6 into Hindi"</span>. The AI will edit the current draft and save it as a new version.
                        </div>
                        <Textarea
                            rows={3}
                            value={refineInstr}
                            onChange={(e) => setRefineInstr(e.target.value)}
                            placeholder="What should change in this draft?"
                            disabled={refining}
                            data-testid="refine-instructions"
                            className="text-sm"
                        />
                        <label className="flex items-start gap-2 mt-3 cursor-pointer text-sm">
                            <input
                                type="checkbox"
                                checked={refineSeniorReview}
                                onChange={(e) => setRefineSeniorReview(e.target.checked)}
                                disabled={refining}
                                className="mt-1 accent-amber-600"
                                data-testid="refine-senior-review"
                            />
                            <span>
                                <span className="inline-flex items-center gap-1.5 font-medium">
                                    <Sparkle size={13} weight="fill" className="text-amber-600" />
                                    Senior Counsel review
                                </span>
                                <span className="block text-xs text-muted-foreground mt-0.5">
                                    Adds a critique pass + auto-redraft. Adds ~30–60s. Recommended for material edits to filings.
                                </span>
                            </span>
                        </label>
                        {refining && refineJob?.stages && (
                            <div className="mt-3" data-testid="refine-progress">
                                <ProgressTimeline
                                    stages={refineJob.stages}
                                    title={refineSeniorReview ? "Refining + Senior review" : "Refining"}
                                    subtitle={refineSeniorReview ? "~60–120s total" : "~10–30s"}
                                />
                            </div>
                        )}
                        <div className="flex justify-end mt-3">
                            <Button
                                onClick={refine}
                                disabled={refining || !refineInstr.trim()}
                                data-testid="refine-submit"
                            >
                                <PaperPlaneTilt size={14} /> {refining ? "Refining…" : "Refine draft"}
                            </Button>
                        </div>
                    </div>

                    {/* Pleading consistency checker */}
                    <ConsistencyCheckPanel draft={draft} />
                </section>

                {/* right: provenance + versions */}
                <aside className="col-span-12 lg:col-span-4 border-l border-border bg-white/50 overflow-y-auto scrollbar-slim p-4" data-testid="draft-right">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Provenance</div>
                    <div className="border border-border rounded-sm p-3 bg-white text-sm space-y-2">
                        <Field label="Matter"><div>{draft.provenance?.matter_title || "—"}</div></Field>
                        <Field label="Documents used">
                            {!draft.provenance?.document_names?.length ? (
                                <span className="text-muted-foreground">—</span>
                            ) : (
                                <ul className="list-disc pl-4">{draft.provenance.document_names.map((n) => <li key={n} className="text-xs">{n}</li>)}</ul>
                            )}
                        </Field>
                        <Field label="Research memo">
                            {draft.provenance?.research_memo_id ? (
                                <span className="chip chip-external">memo: {draft.provenance.research_memo_id.slice(0, 6)}</span>
                            ) : (
                                <span className="text-muted-foreground">—</span>
                            )}
                        </Field>
                        <Field label="Strict mode">{draft.provenance?.strict_mode ? "Yes" : "No"}</Field>
                        <Field label="Model">{draft.provenance?.model}</Field>
                    </div>

                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-5 mb-2">Version history</div>
                    <div className="space-y-2">
                        {(draft.versions || []).slice().reverse().map((v) => (
                            <div key={v.version} className="border border-border rounded-sm p-2 bg-white">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm font-semibold">v{v.version}</div>
                                    <div className="text-[10px] text-muted-foreground">{new Date(v.created_at).toLocaleString()}</div>
                                </div>
                                <div className="text-xs text-muted-foreground">{v.note}</div>
                                {v.version !== draft.current_version && (
                                    <button className="text-xs text-primary mt-1 inline-flex items-center gap-1" onClick={() => revert(v.version)}>
                                        <ArrowClockwise size={11} /> Revert to this
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </aside>
            </div>

            {/* redline dialog */}
            <Dialog open={redlineOpen} onOpenChange={setRedlineOpen}>
                <DialogContent className="max-w-4xl">
                    <DialogHeader>
                        <DialogTitle className="font-serif">Compare versions</DialogTitle>
                    </DialogHeader>
                    <div className="flex items-center gap-2 mb-2">
                        <Select value={verA} onValueChange={setVerA}>
                            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {(draft.versions || []).map((v) => (
                                    <SelectItem key={v.version} value={String(v.version)}>v{v.version}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <span>→</span>
                        <Select value={verB} onValueChange={setVerB}>
                            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {(draft.versions || []).map((v) => (
                                    <SelectItem key={v.version} value={String(v.version)}>v{v.version}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button size="sm" onClick={runRedline}>Compare</Button>
                    </div>
                    <div className="max-h-[60vh] overflow-y-auto font-mono text-xs border border-border rounded-sm p-3 bg-white">
                        {diff?.diff?.map((l, i) => (
                            <div
                                key={i}
                                className={
                                    l.type === "added"
                                        ? "bg-emerald-50 text-emerald-900"
                                        : l.type === "removed"
                                          ? "bg-red-50 text-red-900 line-through"
                                          : ""
                                }
                            >
                                {l.type === "added" ? "+ " : l.type === "removed" ? "- " : "  "}
                                {l.text}
                            </div>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
            <div className="mt-0.5">{children}</div>
        </div>
    );
}

function ConsistencyCheckPanel({ draft }) {
    const [report, setReport] = useState(null);
    const { job, busy, error, start, reset } = useAsyncJob();
    const [includeDocs, setIncludeDocs] = useState(true);

    const run = async () => {
        const j = await start(() => pleadingCheckApi.consistency({ draft_id: draft.id, include_linked_docs: includeDocs }));
        if (j?.status === "completed") {
            setReport(j.result);
            toast.success(`${j.result?.data?.findings?.length || 0} finding(s)`);
        } else if (j?.status === "failed") {
            toast.error(error || j.error || "Check failed");
        }
    };

    const sevColor = { high: "border-destructive/40 bg-destructive/5", medium: "border-amber-300 bg-amber-50/40", low: "border-emerald-300 bg-emerald-50/40" };
    const findings = report?.data?.findings || [];
    const score = report?.data?.overall_score;

    return (
        <div className="mt-6 border border-border bg-white rounded-md p-4 shadow-sm" data-testid="consistency-panel">
            <div className="flex items-center gap-2 mb-1">
                <ShieldCheck size={16} weight="fill" className="text-primary" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Pleading consistency check</div>
            </div>
            <div className="text-xs text-muted-foreground mb-3">
                Checks party-name drift, date/case-no mismatches, contradictions with linked documents, statutory accuracy, prayer alignment.
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2">
                <label className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={includeDocs} onChange={(e) => setIncludeDocs(e.target.checked)} className="accent-primary" data-testid="consistency-include-docs" />
                    Include linked documents in check
                </label>
                <Button onClick={run} disabled={busy} data-testid="consistency-run-btn">
                    <ShieldCheck size={14} /> {busy ? "Reviewing…" : "Run check"}
                </Button>
            </div>
            {busy && job && <JobProgress job={job} />}
            {error && !busy && <div className="mt-2 text-xs text-destructive">{error} <button onClick={reset} className="underline ml-1">retry</button></div>}
            {report && (
                <div className="mt-4 space-y-3" data-testid="consistency-report">
                    {typeof score === "number" && (
                        <div className="flex items-center gap-3 border-b border-border pb-2">
                            <div className="text-2xl font-serif">{score}</div>
                            <div className="flex-1">
                                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Consistency score</div>
                                <div className="text-xs text-muted-foreground italic">{report.data.summary}</div>
                            </div>
                        </div>
                    )}
                    {!findings.length ? (
                        <div className="text-sm text-emerald-700">No inconsistencies detected.</div>
                    ) : (
                        <div className="space-y-2">
                            {findings.map((f, i) => (
                                <div key={i} className={`border rounded-sm p-2 ${sevColor[f.severity] || "border-border"}`} data-testid={`finding-${i}`}>
                                    <div className="flex items-center gap-2 flex-wrap text-xs">
                                        <span className="chip chip-doc">{f.severity}</span>
                                        <span className="font-medium uppercase tracking-[0.15em] text-muted-foreground">{f.category}</span>
                                    </div>
                                    <div className="text-sm mt-1">{f.issue}</div>
                                    {f.quote && <div className="text-xs italic text-muted-foreground mt-1 border-l-2 border-muted-foreground/30 pl-2">"{f.quote}"</div>}
                                    {f.suggested_fix && <div className="text-xs mt-1"><b>Fix:</b> {f.suggested_fix}</div>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

